import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import PDFViewer from '../../components/PDFViewer';
import axios from 'axios';
import { Helmet } from "react-helmet-async";
import Preloader from '../../components/Preloader/Preloader';
import { useSnackbar } from 'notistack';
import bookImage from '../../assets/images/cat1.png';
import { Newspaper, Calendar, Archive, FileText, Sparkles, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

const config = require('../../config.json');

export default function VJToday() {
  const [collapsed, setCollapsed] = useState(true);
  const [selectedItem, setSelectedItem] = useState('');
  const [pdfFile, setPdfFile] = useState('');
  const [loaded, setLoaded] = useState(true);
  const [searchResult, setSearchResult] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [nonArchiveList, setNonArchiveList] = useState([]);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchPublicationList();
  }, []);

  const fetchPublicationList = async () => {
    try {
      setLoaded(false);
      const fetchOption = "PubCategory";
      const fetchKey = "Active";
      const fetchCategory = "VJ Today";
      const response = await axios.get(
        `${config.api.invokeURL}/dashboard/publications?fetchOption=${fetchOption}&fetchKey=${fetchKey}&fetchCategory=${fetchCategory}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );
      const tempRes = response.data.Items || [];
      tempRes.sort((a, b) => new Date(b.regDate || b.issueYear || 0) - new Date(a.regDate || a.issueYear || 0));

      const currentDatetime = new Date();
      const cutoffYear = (currentDatetime.getFullYear() - 1).toString();
      const cutoffDate = new Date(`${cutoffYear}-01-01`);

      const filteredData = tempRes.filter(item => item.regDate ? new Date(item.regDate) > cutoffDate : true);
      const archiveData = tempRes.filter(item => item.regDate ? new Date(item.regDate) <= cutoffDate : false);

      setArchivedList(archiveData);
      setNonArchiveList(filteredData);
      setSearchResult(filteredData);
      if (filteredData.length > 0) {
        enqueueSnackbar(`${filteredData.length} active records found in VJ Today`, { variant: 'success' });
      }
      setLoaded(true);
      window.scrollTo(0, 0);
    } catch (error) {
      enqueueSnackbar(`Error occurred: ${error.message}`, { variant: 'warning' });
      setLoaded(true);
    }
  };

  const toggle = () => {
    setCollapsed(!collapsed);
  };

  const handlePDFViewer = async (event, item) => {
    if (event) event.preventDefault();
    setSelectedItem(item);
    setLoaded(false);
    try {
      const fetchFile = item.id + ".pdf";
      const bucketName = 'docs.vidyajyoticollege.in/pdfs/vjtoday';
      const response = await axios.get(
        `${config.api.invokeURL}/presigned/static?fileName=${fetchFile}&bucketName=${bucketName}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );
      setPdfFile(response.data.downloadURL);
      setCollapsed(false);
      setLoaded(true);
    } catch (error) {
      enqueueSnackbar(`Unable to load PDF: ${error.message}`, { variant: 'error' });
      setLoaded(true);
    }
  };

  const handleImageError = (event) => {
    event.target.src = bookImage;
  };

  const toggleArchiveMode = (showArchive) => {
    setIsArchiveView(showArchive);
    if (showArchive) {
      setSearchResult(archivedList.length > 0 ? archivedList : nonArchiveList);
      enqueueSnackbar(`${archivedList.length} archive issues loaded`, { variant: 'info' });
    } else {
      setSearchResult(nonArchiveList);
      enqueueSnackbar(`${nonArchiveList.length} current issues loaded`, { variant: 'success' });
    }
  };

  return (
    <>
      <Helmet>
        <title>VJ Today Newsletter - Vidyajyoti College of Theology - Delhi</title>
        <meta name="description" content="VJ Today - The Student Newsletter and Pastoral Chronicler of Vidyajyoti" />
        <meta name="keywords" content="VJ Today, Vidyajyoti newsletter, student journalism, theology updates" />
        <link rel="canonical" href="/publications/vjtoday" />
      </Helmet>

      <Preloader show={!loaded} />

      {/* PDF Modal */}
      <Modal fullscreen isOpen={!collapsed} toggle={toggle} style={{ top: '0%' }}>
        <ModalHeader
          className="d-flex justify-content-between align-items-center"
          style={{ background: '#1a2b56', color: '#fff' }}
          close={
            <button className="btn btn-sm btn-outline-light rounded-pill px-3" onClick={toggle}>
              ✕ Close
            </button>
          }
        >
          <span className="fw-bold">
            VJ Today: {selectedItem ? `${selectedItem.issueMonth || ''} ${selectedItem.issueYear || ''} (${selectedItem.issue || ''})` : 'Newsletter Viewer'}
          </span>
        </ModalHeader>
        <ModalBody className="p-0">
          <PDFViewer pdfFile={pdfFile} />
        </ModalBody>
      </Modal>

      <div className="py-5" style={{ background: '#f8fafc', minHeight: '80vh' }}>
        <div className="container">
          {/* Page Header */}
          <div className="text-center mx-auto pb-4" style={{ maxWidth: '750px' }}>
            <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(121, 3, 29, 0.2)' }}>
              <Sparkles size={14} /> Student Newsletter
            </div>
            <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
              VJ Today Newsletter
            </h1>
            <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
              The dynamic chronicle of campus life, theological debates, apostolate encounters, student achievements, and community reflections at Vidyajyoti College of Theology.
            </p>

            {/* Filter Pill Controls */}
            <div className="d-inline-flex p-1 rounded-pill bg-white shadow-sm border mt-3">
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-4 fw-semibold ${!isArchiveView ? 'text-white shadow-sm' : 'text-muted'}`}
                style={{ background: !isArchiveView ? '#1a2b56' : 'transparent', border: 'none' }}
                onClick={() => toggleArchiveMode(false)}
              >
                <Newspaper size={14} className="me-1.5" /> Latest Issues ({nonArchiveList.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-4 fw-semibold ${isArchiveView ? 'text-white shadow-sm' : 'text-muted'}`}
                style={{ background: isArchiveView ? '#79031d' : 'transparent', border: 'none' }}
                onClick={() => toggleArchiveMode(true)}
              >
                <Archive size={14} className="me-1.5" /> Past Archives ({archivedList.length})
              </button>
            </div>
          </div>

          {/* Issues Grid */}
          <div className="row g-4 justify-content-center">
            {searchResult && searchResult.length > 0 ? (
              searchResult.map((item, index) => (
                <motion.div
                  key={index}
                  className="col-sm-6 col-md-4 col-lg-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                >
                  <div
                    className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white position-relative d-flex flex-column"
                    style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease', cursor: 'pointer' }}
                    onClick={(e) => handlePDFViewer(e, item)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div className="position-relative overflow-hidden" style={{ height: '280px', background: '#f1f5f9' }}>
                      <img
                        src={`${config.s3.pubPoster}${item.id}.jpeg`}
                        className="w-100 h-100"
                        alt={item.issue || 'VJ Today Issue'}
                        onError={handleImageError}
                        style={{ objectFit: 'cover' }}
                      />
                      <span
                        className="position-absolute top-0 end-0 m-3 px-2.5 py-1 rounded-pill text-white small fw-bold"
                        style={{ background: '#79031d', fontSize: '0.75rem' }}
                      >
                        {item.issueYear || 'Archive'}
                      </span>
                    </div>

                    <div className="card-body p-3 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center gap-1.5 text-muted small mb-1">
                          <Calendar size={13} /> {item.issueMonth || ''} {item.issueYear || ''}
                        </div>
                        <h6 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>
                          {item.issue ? `VJ Today - ${item.issue}` : 'VJ Today Newsletter'}
                        </h6>
                      </div>

                      <div className="mt-3 pt-2 border-top">
                        <span 
                          className="btn btn-sm w-100 rounded-pill fw-semibold d-flex align-items-center justify-content-center gap-2"
                          style={{ background: 'rgba(26,43,86,0.06)', color: '#1a2b56', border: '1px solid rgba(26,43,86,0.15)' }}
                        >
                          <FileText size={14} /> View Newsletter <ExternalLink size={12} />
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              loaded && (
                <div className="col-12 text-center py-5">
                  <div className="p-4 rounded-4 bg-white shadow-sm d-inline-block" style={{ maxWidth: '450px' }}>
                    <Newspaper size={36} className="text-muted mb-2" />
                    <h5 className="fw-semibold text-muted">No newsletter records found</h5>
                    <p className="small text-muted mb-0">Issues will load automatically once retrieved from the archive.</p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
}
